import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import { Container } from '../common/Container';

function SwiperButtons({ swiperRef }) {
  return (
    <div className="hidden lg:flex gap-[10px] justify-end">
      <button
        swiperRef={swiperRef}
        onClick={() => swiperRef.current?.slidePrev()}
        className="hover:scale-105 transition-all duration-500 rounded-lg w-12 h-12 border border-primary text-primary flex justify-center items-center"
      >
        <ChevronLeftIcon className="w-6 h-6" />
      </button>

      <button
        swiperRef={swiperRef}
        onClick={() => swiperRef.current?.slideNext()}
        className="hover:scale-105 transition-all duration-500 rounded-lg w-12 h-12 border border-primary text-primary flex justify-center items-center"
      >
        <ChevronRightIcon className="w-6 h-6" />
      </button>
    </div>
  );
}

function Testimonial({ avatar, name, role, text }) {
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState('0px');
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      setContentHeight(expanded ? `${textRef.current.scrollHeight}px` : '179px');
    }
  }, [expanded]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="min-w-[250px] xl:w-[523px] min-h-[330px] rounded-[22px] shadow-lg px-[47px] pt-[35px] pb-[42px] font-helvetica">
      <div className="flex flex-col justify-between gap-9">
        <div className="flex flex-col-reverse items-center sm:items-start sm:flex-row justify-between">
          <div className="flex flex-col">
            <h3 className="text-base w-full text-center sm:text-left font-semibold text-primary">{name}</h3>
            <p className="text-sm w-full text-center sm:text-left max-h-[19px]">{role}</p>
          </div>

          <Image
            src={avatar}
            alt="Avatar"
            width={100}
            height={100}
            className="object-center rounded-full w-14 h-14"
          />
        </div>

        <div className="min-h-[179px]">
          <div
            ref={textRef}
            style={{ maxHeight: contentHeight }}
            className={`w-full transition-all duration-500 ease-in-out overflow-hidden ${
              expanded ? 'h-auto' : 'max-h-[179px]'
            }`}
          >
            <p className="lg:text-justify">
              {text}
            </p>
          </div>

          {textRef.current?.scrollHeight > 179 && (
            <button onClick={toggleExpanded} className="w-full mt-2 text-primary underline">
              {expanded ? 'Mostrar menos' : 'Mostrar mais'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function Testimonials({ testimonies }) {
  const swiperRef = useRef(null);

  const testimonialsData = [...testimonies].map((t, i) => ({
    avatar: t.f_photo.url,
    name: t.name,
    role: t.office,
    text: t.description,
  }));

  return (
    <Container>
      <div className="lg:mb-36 pt-20">
        <div className="flex flex-col gap-8 ">
          <h2 className="relative text-2xl font-montserrat font-bold text-center lg:text-left text-black md:text-3xl lg:text-4xl">
            O que dizem sobre nós:
          </h2>
        </div>

        <div>
          <SwiperButtons swiperRef={swiperRef} />

          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Navigation, Pagination, A11y, Autoplay]}
            grabCursor={true}
            loop={true}
            centeredSlides={false}
            spaceBetween={44}
            slidesPerView={1}
            breakpoints={{
              144: {
                slidesPerView: 1,
                centeredSlides: true,
                spaceBetween: 0,
              },
              742: {
                centeredSlides: false,
                spaceBetween: 44,
                slidesPerView: 1.2,
              },
              820: {
                slidesPerView: 2,
              },
              1440: {
                slidesPerView: 2.2,
              },
              1564: {
                slidesPerView: 2.4,
              },
              1708: {
                slidesPerView: 2.8,
              },
              1878: {
                slidesPerView: 3,
              },
              2012: {
                slidesPerView: 3.2,
              },
              2138: {
                slidesPerView: 3.6,
              },
              2446: {
                slidesPerView: 4,
              },
            }}
            initialSlide={0}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
          >
            {testimonialsData.map((testimonial, index) => (
              <SwiperSlide key={index} className="flex justify-center h-full px-6 py-10">
                <Testimonial key={index} {...testimonial} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </Container>
  );
}
